import { useState, type FormEvent } from "react";
import s from "./Capacity.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useClosureCost, useCriticalPositions } from "../../api/queries";
import { hasPermission, PERMISSION, type ClosureQuery } from "../../api/dataSource";
import { disposition, verdict, VERDICT_LABEL, type ClosureCost, type CriticalReport } from "../../domain/capacity";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Skeleton,
  Table,
} from "../../design/components";
import { t, tn } from "../../i18n";

type View = "one" | "all";

/* a cleared or half-typed date is not a date: answer with nothing rather than crash the tab */
const isoOrNull = (v: string): string | null => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
const isoLocal = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

/**
 * What closing a position costs.
 *
 * The screen is built to contradict the metric every airport dashboard uses.
 * Counting the flights a stand serves misleads in both directions, so the
 * headline here is never the movement count — it is how many aircraft have
 * NOWHERE ELSE TO GO. The busiest stand on the estate is often the cheapest to
 * close, and the "flexible" list is where maintenance should actually go.
 *
 * Everything on this page is a COUNTERFACTUAL and says so. "These flights
 * would have used this position" describes a world that did not happen, and
 * printed beside a real figure it will be read as one.
 */
export function CapacityPage() {
  const { site, principal } = useSession();
  const may = hasPermission(principal, PERMISSION.capacityRead);
  const [view, setView] = useState<View>("one");

  const now = new Date();
  const end = new Date(now.getTime() + 12 * 3600_000);
  const [form, setForm] = useState({
    positionCode: "",
    from: isoLocal(now),
    to: isoLocal(end),
  });
  const [asked, setAsked] = useState<ClosureQuery | null>(null);
  const cost = useClosureCost(asked);
  const critical = useCriticalPositions(isoOrNull(form.from) ?? "", isoOrNull(form.to) ?? "");

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const ask = (e: FormEvent) => {
    e.preventDefault();
    const from = isoOrNull(form.from);
    const to = isoOrNull(form.to);
    if (!form.positionCode.trim() || !from || !to) return;
    setAsked({ siteId: site.id, positionCode: form.positionCode.trim(), from, to });
  };

  return (
    <Page
      eyebrow="Deep thinking"
      title="What closing a stand costs"
      actions={
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="View"
          options={[
            { value: "one", label: "One position" },
            { value: "all", label: "Which cannot close" },
          ]}
        />
      }
    >
      <Card
        title="The window"
        sub="The cost of a closure is the traffic that has nowhere else to go — not the traffic it carries."
      >
        <form className={s.form} onSubmit={ask}>
          {view === "one" && (
            <Field label="Position">
              <Input
                value={form.positionCode}
                onChange={set("positionCode")}
                placeholder={t("e.g. A12")}
                aria-label={t("Position")}
              />
            </Field>
          )}
          <Field label="From">
            <Input type="datetime-local" value={form.from} onChange={set("from")} aria-label={t("From")} />
          </Field>
          <Field label="To">
            <Input type="datetime-local" value={form.to} onChange={set("to")} aria-label={t("To")} />
          </Field>
          {view === "one" && (
            <Button type="submit" variant="primary" disabled={!form.positionCode.trim() || !may}>
              {t("Cost this closure")}
            </Button>
          )}
        </form>
      </Card>

      {view === "one" && (
        <>
          {!asked && (
            <EmptyState>
              {t(
                "Name a position and a window. The answer is a disposition, not a count — and only the last number in it is worth an argument.",
              )}
            </EmptyState>
          )}
          {asked && cost.isPending && <Skeleton rows={5} />}
          {asked && cost.isError && <ErrorState error={cost.error} retry={() => cost.refetch()} />}
          {cost.data && <Closure cost={cost.data} />}
        </>
      )}

      {view === "all" && (
        <>
          {critical.isPending && <Skeleton rows={6} />}
          {critical.isError && <ErrorState error={critical.error} retry={() => critical.refetch()} />}
          {critical.data && <Critical report={critical.data} />}
        </>
      )}
    </Page>
  );
}

function Closure({ cost }: { cost: ClosureCost }) {
  const v = verdict(cost);
  return (
    <>
      <div
        data-testid="closure-verdict"
        className={`${s.verdict} ${
          v === "stranding" ? s.stranding : v === "bussing" ? s.bussing : v === "free" ? s.free : s.unknown
        }`}
      >
        <div className={s.eyebrow}>{t(VERDICT_LABEL[v])}</div>
        {cost.known ? (
          <>
            <div className={s.headline}>
              {cost.stranded > 0 ? (
                <>
                  <span className={s.big}>{cost.stranded}</span> {t("with nowhere to go")}
                </>
              ) : (
                <>
                  <span className={s.big}>{cost.movements}</span>{" "}
                  {tn(cost.movements, "movement, all placed", "movements, all placed")}
                </>
              )}
            </div>
            <div className={s.reason}>{disposition(cost)}</div>
          </>
        ) : (
          <>
            <div className={s.headline}>{t("This closure cannot be costed")}</div>
            <div className={s.reason}>{cost.whyNot}</div>
          </>
        )}
        {/* The counterfactual, never omitted. */}
        {cost.assumption && <div className={s.caveat}>{cost.assumption}</div>}
      </div>

      {cost.detail.length > 0 && (
        <Card
          title="Where each movement would go"
          sub="The reason is the answer; the destination is a consequence of it."
        >
          <Table>
            <thead>
              <tr>
                <th>{t("Flight")}</th>
                <th>{t("Size")}</th>
                <th>{t("Would move to")}</th>
                <th>{t("Why")}</th>
              </tr>
            </thead>
            <tbody>
              {cost.detail.map((d, i) => (
                <tr key={`${d.flight}-${i}`} className={d.movesTo ? undefined : s.strandedRow}>
                  <td>{d.flight ?? "—"}</td>
                  <td>{d.sizeCode ?? "—"}</td>
                  {/* Not a dash: an aircraft with nowhere to go is the finding. */}
                  <td>{d.movesTo ?? <b>{t("nowhere")}</b>}</td>
                  <td className={s.reason}>{d.why}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

function Critical({ report }: { report: CriticalReport }) {
  if (!report.known) {
    return <EmptyState>{report.whyNot ?? "The schedule does not cover this window."}</EmptyState>;
  }
  return (
    <>
      <Card
        title={t("Cannot be closed — {n}", { n: report.critical.length })}
        sub="Closing one of these strands traffic. They are not necessarily the busiest stands."
      >
        {report.critical.length === 0 ? (
          <EmptyState>Nothing on this estate is a single point of failure in this window.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Position")}</th>
                <th className={s.num}>{t("Movements")}</th>
                <th className={s.num}>{t("Stranded")}</th>
              </tr>
            </thead>
            <tbody>
              {report.critical.map((r) => (
                <tr key={r.position} className={s.strandedRow}>
                  <td>{r.position}</td>
                  <td className={s.num}>{r.movements ?? "—"}</td>
                  <td className={s.num}>
                    <b>{r.stranded}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card
        title={t("Can be closed — {n}", { n: report.flexible.length })}
        sub="This is where maintenance should go first, and the list is usually longer than operators expect."
      >
        {report.flexible.length === 0 ? (
          <EmptyState>No position in this window can be closed without stranding traffic.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Position")}</th>
                <th className={s.num}>{t("Movements")}</th>
                <th>{t("Cost of closing")}</th>
              </tr>
            </thead>
            <tbody>
              {report.flexible.map((r) => (
                <tr key={r.position}>
                  <td>{r.position}</td>
                  <td className={s.num}>{r.movements ?? "—"}</td>
                  <td>{r.bussed ? t("{n} bussed", { n: r.bussed }) : t("absorbed entirely")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {report.note && <p className={s.reason}>{report.note}</p>}
    </>
  );
}
